export type ActivityCategory = 'workspace' | 'generation' | 'api' | 'error' | 'performance';
export type ActivityEvent = {
  id: string;
  timestamp: number;
  category: ActivityCategory;
  action: string;
  metadata?: Record<string, unknown>;
};

const DB_NAME = 'PicassoActivity';
const STORE_NAME = 'events';
const MAX_EVENTS = 1000;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('timestamp', 'timestamp');
      store.createIndex('category', 'category');
    };
  });
}

export async function logActivity(category: ActivityCategory, action: string, metadata?: Record<string, unknown>): Promise<void> {
  try {
    const db = await openDB();
    const event: ActivityEvent = { id: `activity-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, timestamp: Date.now(), category, action, metadata };
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(event);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
    const count = await new Promise<number>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
    if (count > MAX_EVENTS) {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const cursorRequest = tx.objectStore(STORE_NAME).index('timestamp').openCursor();
        let remaining = count - MAX_EVENTS;
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor && remaining > 0) { cursor.delete(); remaining -= 1; cursor.continue(); }
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    }
    db.close();
  } catch {
    // Observability must never interrupt the product workflow.
  }
}

export async function queryActivity(category?: ActivityCategory, limit = 100): Promise<ActivityEvent[]> {
  try {
    const db = await openDB();
    const events = await new Promise<ActivityEvent[]>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = category ? tx.objectStore(STORE_NAME).index('category').getAll(category) : tx.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve(request.result as ActivityEvent[]);
      request.onerror = () => resolve([]);
    });
    db.close();
    return events.sort((a, b) => b.timestamp - a.timestamp).slice(0, limit);
  } catch {
    return [];
  }
}
